import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Eye, FilePenLine, Trash2, Filter, AArrowUp, AArrowDown, Check, ChevronDown } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectOption } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem, DropdownMenuCheckboxItem } from "../../components/ui/dropdown-menu";
import DataTable from "../../components/shared/DataTable";
import PageHeader from "../../components/shared/PageHeader";
import StatusBadge from "../../components/shared/StatusBadge";
import SearchBar from "../../components/shared/SearchBar";
import RefreshButton from "../../components/shared/RefreshButton";
import { formatNumberToDollar, countActiveFiltersAndSort } from "../../utils/util";
import {
  listAllPreQuoteBrands,
  listAllPreQuoteSets,
  listAllPreQuotedCooks,
  updatePreQuotedCooksStatus,
  createPreQuotedCooks,
  updatePreQuotedCooks,
} from "../../reducers/prequotedcooks.slice";
import { AuthContext } from "../../context/authContext";
import useMediaQuery from "../../components/shared/useMediaQuery";

const PQC_STATUSES = ["ACTIVE", "INACTIVE"];

const INTEGER_KEYS = ["Backspace","Delete","ArrowLeft","ArrowRight","Tab","Enter","Home","End"];
const PRICE_KEYS = [...INTEGER_KEYS, "."];

const blockInvalidInt = (e) => {
  const bad = ["e","E",".",",","+","-"];
  if (bad.includes(e.key) || (!/^\d$/.test(e.key) && !INTEGER_KEYS.includes(e.key) && !e.ctrlKey && !e.metaKey)) e.preventDefault();
};
const blockInvalidPrice = (e) => {
  const bad = ["e","E",",","+","-"];
  if (e.key === "." && e.target.value.includes(".")) { e.preventDefault(); return; }
  if (bad.includes(e.key) || (!/^\d$/.test(e.key) && e.key !== "." && !PRICE_KEYS.includes(e.key) && !e.ctrlKey && !e.metaKey)) e.preventDefault();
};

const CatalogSelect = ({
  id,
  value,
  placeholder,
  disabled = false,
  options = [],
  emptyLabel,
  onValueChange,
}) => {
  const selectedOption = options.find((option) => option.id === value);
  const displayValue = selectedOption?.name || emptyLabel || placeholder;
  const isMuted = !selectedOption && !emptyLabel;

  const menuItems = emptyLabel
    ? [{ id: "", name: emptyLabel }, ...options]
    : options;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          style={{
            width: "100%",
            height: 44,
            justifyContent: "space-between",
            padding: "0 13px 0 14px",
            borderRadius: 12,
            background: disabled ? "rgba(255,255,255,0.025)" : "rgba(255,255,255,0.035)",
            border: "1px solid rgba(255,255,255,0.12)",
            color: isMuted ? "#8b9296" : "#fff",
            boxShadow: "inset 0 1px 2px rgba(0,0,0,0.15)",
            fontSize: 14,
            opacity: disabled ? 0.58 : 1,
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {disabled ? placeholder : displayValue}
          </span>
          <ChevronDown size={15} style={{ color: "#8b9296", flex: "0 0 auto" }} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        style={{
          width: "var(--radix-dropdown-menu-trigger-width)",
          maxHeight: 260,
          padding: 6,
        }}
      >
        {menuItems.length === 0 ? (
          <DropdownMenuItem disabled style={{ cursor: "default", color: "#8b9296" }}>
            No options available
          </DropdownMenuItem>
        ) : (
          menuItems.map((option) => {
            const active = option.id === value;
            return (
              <DropdownMenuItem
                key={option.id || "__empty"}
                active={active}
                onClick={() => onValueChange(option.id)}
                style={{
                  minHeight: 36,
                  justifyContent: "space-between",
                  fontSize: 13.5,
                }}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  {option.name}
                </span>
                {active && <Check size={14} style={{ color: "#00ADB5", flex: "0 0 auto" }} />}
              </DropdownMenuItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const PqcCreateForm = ({ onSuccess, showToast, isMobile }) => {
  const dispatch = useDispatch();
  const [showLoader, setShowLoader] = useState(false);
  const [brands, setBrands] = useState([]);
  const [sets, setSets] = useState([]);
  const [formData, setFormData] = useState({
    item_name: "", item_link: "", item_image_url: "", upc: "", retailer_name: "",
    total_quantity: "", max_per_user_quantity: "", description: "",
    tags: "", buy_price: "", cashout_price: "", status: "ACTIVE",
    brand_id: "", set_id: "",
  });
  const [formErrors, setFormErrors] = useState({ max_per_user_quantity: "", total_quantity: "", buy_price: "", cashout_price: "", upc: "" });

  useEffect(() => {
    dispatch(listAllPreQuoteBrands()).then((res) => {
      if (res?.error && Object.keys(res.error).length > 0) { showToast(res.error.message, "error"); return; }
      setBrands(res?.payload || []);
    });
  }, []);

  useEffect(() => {
    if (!formData.brand_id) { setSets([]); return; }
    dispatch(listAllPreQuoteSets({ brand_id: formData.brand_id })).then((res) => {
      if (res?.error && Object.keys(res.error).length > 0) { showToast(res.error.message, "error"); return; }
      setSets(res?.payload || []);
    });
  }, [formData.brand_id]);

  const handleFormData = (e) => {
    const { name, value } = e.target;
    if (name === "upc") {
      const v = value.replace(/[^0-9]/g, "");
      const err = v.length > 0 && (v.length < 11 || v.length > 14) ? "UPC should be 11–14 digits long" : v !== value ? "UPC must contain only numbers" : "";
      setFormErrors((p) => ({ ...p, upc: err }));
      setFormData((p) => ({ ...p, upc: v }));
      return;
    }
    if (["max_per_user_quantity", "total_quantity"].includes(name)) {
      if (value === "") { setFormData((p) => ({ ...p, [name]: "" })); setFormErrors((p) => ({ ...p, [name]: "" })); return; }
      const parsed = parseInt(value, 10);
      if (isNaN(parsed) || parsed <= 0 || parsed !== Number(value)) { setFormErrors((p) => ({ ...p, [name]: "Must be a positive whole number (min 1)" })); return; }
      setFormData((p) => {
        const next = { ...p, [name]: parsed };
        if (name === "total_quantity") setFormErrors((pe) => ({ ...pe, [name]: "", max_per_user_quantity: next.max_per_user_quantity > parsed ? "Max per user quantity cannot exceed total quantity" : "" }));
        else setFormErrors((pe) => ({ ...pe, [name]: parsed > Number(p.total_quantity) ? "Max per user quantity cannot exceed total quantity" : "" }));
        return next;
      });
      return;
    }
    if (["buy_price", "cashout_price"].includes(name)) {
      if (value === "") { setFormData((p) => ({ ...p, [name]: "" })); setFormErrors((p) => ({ ...p, [name]: "" })); return; }
      const parsed = parseFloat(value);
      if (name === "buy_price" && (isNaN(parsed) || parsed <= 0)) { setFormErrors((p) => ({ ...p, buy_price: "Buy price must be greater than zero" })); return; }
      if (name === "cashout_price" && (isNaN(parsed) || parsed < 0)) { setFormErrors((p) => ({ ...p, cashout_price: "Must be a positive number" })); return; }
      setFormData((p) => ({ ...p, [name]: value }));
      setFormErrors((p) => ({ ...p, [name]: "" }));
      return;
    }
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = () => {
    setShowLoader(true);
    const payload = { ...formData };
    if (!payload.max_per_user_quantity) delete payload.max_per_user_quantity;
    if (!payload.brand_id) delete payload.brand_id;
    if (!payload.set_id) delete payload.set_id;
    if (!payload.item_image_url?.trim()) delete payload.item_image_url;
    dispatch(createPreQuotedCooks(payload)).then((res) => {
      setShowLoader(false);
      if (res?.error && Object.keys(res.error).length > 0) { showToast(res.error.message, "error"); return; }
      onSuccess();
    });
  };

  const isFormIncomplete =
    !formData.item_link || !formData.item_name || !formData.upc || !formData.cashout_price ||
    !formData.retailer_name || !formData.description || formData.buy_price === "" ||
    parseFloat(formData.buy_price) <= 0 || !formData.total_quantity ||
    !formData.brand_id ||
    !!formErrors.max_per_user_quantity || !!formErrors.upc || !!formErrors.buy_price;

  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div
          style={{
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 12,
            padding: 14,
            background: "rgba(255,255,255,0.02)",
          }}
        >
          <div style={{ color: "#fff", fontSize: 13, fontWeight: 800, marginBottom: 12 }}>
            Catalog Placement
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
            <div>
              <Label htmlFor="brand_id">Brand *</Label>
              <CatalogSelect
                id="brand_id"
                value={formData.brand_id}
                placeholder="Select a brand"
                options={brands}
                onValueChange={(brandId) => setFormData((p) => ({ ...p, brand_id: brandId, set_id: "" }))}
              />
            </div>
            <div>
              <Label htmlFor="set_id">Set</Label>
              <CatalogSelect
                id="set_id"
                value={formData.set_id}
                placeholder={formData.brand_id ? "Select a set" : "Select a brand first"}
                emptyLabel="No set"
                options={sets}
                disabled={!formData.brand_id}
                onValueChange={(setId) => setFormData((p) => ({ ...p, set_id: setId }))}
              />
            </div>
          </div>
        </div>
        <div>
          <Label htmlFor="upc">UPC</Label>
          <Input id="upc" name="upc" value={formData.upc} placeholder="Enter UPC" onChange={handleFormData} onWheel={(e) => e.target.blur()} />
          {formErrors.upc && <p style={{ fontSize: 11, color: "#f27059", marginTop: 4 }}>{formErrors.upc}</p>}
        </div>
        <div>
          <Label htmlFor="item_name">Item Name</Label>
          <Input id="item_name" name="item_name" value={formData.item_name} placeholder="Item Name" onChange={handleFormData} />
        </div>
        <div>
          <Label htmlFor="item_link">Item Link</Label>
          <Input id="item_link" name="item_link" value={formData.item_link} placeholder="Enter Product Link" onChange={handleFormData} />
        </div>
        <div>
          <Label htmlFor="item_image_url">Image URL</Label>
          <Input id="item_image_url" name="item_image_url" value={formData.item_image_url} placeholder="Paste product image link" onChange={handleFormData} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
          <div>
            <Label htmlFor="total_quantity">Quantity</Label>
            <Input id="total_quantity" type="number" name="total_quantity" value={formData.total_quantity} placeholder="Quantity" onChange={handleFormData} onKeyDown={blockInvalidInt} onPaste={(e) => { if (!/^\d+$/.test(e.clipboardData.getData("text"))) e.preventDefault(); }} onWheel={(e) => e.target.blur()} />
            {formErrors.total_quantity && <p style={{ fontSize: 11, color: "#f27059", marginTop: 4 }}>{formErrors.total_quantity}</p>}
          </div>
          <div>
            <Label htmlFor="max_per_user_quantity">Max Qty Per User</Label>
            <Input id="max_per_user_quantity" type="number" name="max_per_user_quantity" value={formData.max_per_user_quantity} placeholder="Max Qty" onChange={handleFormData} onKeyDown={blockInvalidInt} onPaste={(e) => { if (!/^\d+$/.test(e.clipboardData.getData("text"))) e.preventDefault(); }} onWheel={(e) => e.target.blur()} />
            {formErrors.max_per_user_quantity && <p style={{ fontSize: 11, color: "#f27059", marginTop: 4 }}>{formErrors.max_per_user_quantity}</p>}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
          <div>
            <Label htmlFor="buy_price">Buy Price</Label>
            <Input id="buy_price" type="number" name="buy_price" value={formData.buy_price} placeholder="Buy Price" onChange={handleFormData} onKeyDown={blockInvalidPrice} onPaste={(e) => { if (!/^\d*\.?\d*$/.test(e.clipboardData.getData("text"))) e.preventDefault(); }} onWheel={(e) => e.target.blur()} />
            {formErrors.buy_price && <p style={{ fontSize: 11, color: "#f27059", marginTop: 4 }}>{formErrors.buy_price}</p>}
          </div>
          <div>
            <Label htmlFor="cashout_price">Cash-Out Price</Label>
            <Input id="cashout_price" type="number" name="cashout_price" value={formData.cashout_price} placeholder="Cash-Out Price" onChange={handleFormData} onKeyDown={blockInvalidPrice} onPaste={(e) => { if (!/^\d*\.?\d*$/.test(e.clipboardData.getData("text"))) e.preventDefault(); }} onWheel={(e) => e.target.blur()} />
            {formErrors.cashout_price && <p style={{ fontSize: 11, color: "#f27059", marginTop: 4 }}>{formErrors.cashout_price}</p>}
          </div>
        </div>
        <div>
          <Label htmlFor="retailer_name">Retailer Name</Label>
          <Input id="retailer_name" name="retailer_name" value={formData.retailer_name} placeholder="Enter Retailer Name" onChange={handleFormData} />
        </div>
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea id="description" name="description" value={formData.description} placeholder="Enter Description" onChange={handleFormData} />
        </div>
        <div>
          <Label htmlFor="tags">Tags</Label>
          <Input id="tags" name="tags" value={formData.tags} placeholder="Enter Tags" onChange={handleFormData} />
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Button disabled={isFormIncomplete || showLoader} onClick={handleSubmit}>
          {showLoader ? "Submitting…" : "Submit"}
        </Button>
      </div>
    </div>
  );
};

const PqcEditForm = ({ editData, onSuccess, showToast }) => {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const dispatch = useDispatch();
  const [showLoader, setShowLoader] = useState(false);
  const [brands, setBrands] = useState([]);
  const [sets, setSets] = useState([]);
  const [formErrors, setFormErrors] = useState({ max_per_user_quantity: "", total_quantity: "", buy_price: "", cashout_price: "", upc: "" });
  const [formData, setFormData] = useState({
    item_name: editData?.item_name ?? "",
    item_link: editData?.item_link ?? "",
    item_image_url: editData?.item_image_url ?? "",
    upc: editData?.upc ?? "",
    retailer_name: editData?.retailer_name ?? "",
    total_quantity: editData?.total_quantity ?? "",
    description: editData?.description ?? "",
    tags: editData?.tags ?? "",
    buy_price: editData?.buy_price ?? "",
    cashout_price: editData?.cashout_price ?? "",
    max_per_user_quantity: editData?.max_per_user_quantity ?? "",
    status: editData?.status ?? "ACTIVE",
    brand_id: editData?.brand_id ?? "",
    set_id: editData?.set_id ?? "",
  });

  useEffect(() => {
    dispatch(listAllPreQuoteBrands()).then((res) => {
      if (res?.error && Object.keys(res.error).length > 0) { showToast(res.error.message, "error"); return; }
      setBrands(res?.payload || []);
    });
  }, []);

  useEffect(() => {
    if (!formData.brand_id) { setSets([]); return; }
    dispatch(listAllPreQuoteSets({ brand_id: formData.brand_id })).then((res) => {
      if (res?.error && Object.keys(res.error).length > 0) { showToast(res.error.message, "error"); return; }
      setSets(res?.payload || []);
    });
  }, [formData.brand_id]);

  useEffect(() => {
    if (formData.total_quantity < editData?.available_quantity) {
      setFormErrors((p) => ({ ...p, total_quantity: "Total quantity cannot be lower than approved quantity" }));
    }
    if (formData.max_per_user_quantity > formData.total_quantity) {
      setFormErrors((p) => ({ ...p, max_per_user_quantity: "Max per user quantity cannot exceed total quantity" }));
    }
  }, []);

  const handleFormData = (e) => {
    const { name, value } = e.target;
    if (name === "upc") {
      const v = value.replace(/[^0-9]/g, "");
      const err = v.length > 0 && (v.length < 11 || v.length > 14) ? "UPC should be 11–14 digits long" : v !== value ? "UPC must contain only numbers" : "";
      setFormErrors((p) => ({ ...p, upc: err }));
      setFormData((p) => ({ ...p, upc: v }));
      return;
    }
    if (["max_per_user_quantity", "total_quantity"].includes(name)) {
      if (value === "") { setFormData((p) => ({ ...p, [name]: "" })); setFormErrors((p) => ({ ...p, [name]: "" })); return; }
      const parsed = parseInt(value, 10);
      if (isNaN(parsed) || parsed <= 0 || parsed !== Number(value)) { setFormErrors((p) => ({ ...p, [name]: "Must be a positive whole number (min 1)" })); return; }
      setFormData((p) => {
        const next = { ...p, [name]: parsed };
        if (name === "total_quantity") setFormErrors((pe) => ({ ...pe, [name]: "", max_per_user_quantity: next.max_per_user_quantity > parsed ? "Max per user quantity cannot exceed total quantity" : "" }));
        else setFormErrors((pe) => ({ ...pe, [name]: parsed > Number(p.total_quantity) ? "Max per user quantity cannot exceed total quantity" : "" }));
        return next;
      });
      return;
    }
    if (["buy_price", "cashout_price"].includes(name)) {
      if (value === "") { setFormData((p) => ({ ...p, [name]: "" })); setFormErrors((p) => ({ ...p, [name]: "" })); return; }
      const parsed = parseFloat(value);
      if (name === "buy_price" && (isNaN(parsed) || parsed <= 0)) { setFormErrors((p) => ({ ...p, buy_price: "Buy price must be greater than zero" })); return; }
      if (name === "cashout_price" && (isNaN(parsed) || parsed < 0)) { setFormErrors((p) => ({ ...p, cashout_price: "Must be a positive number" })); return; }
      setFormData((p) => ({ ...p, [name]: value }));
      setFormErrors((p) => ({ ...p, [name]: "" }));
      return;
    }
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = () => {
    setShowLoader(true);
    const payload = { ...formData, id: editData.id };
    if (!payload.max_per_user_quantity || payload.max_per_user_quantity === "0" || payload.max_per_user_quantity === 0) {
      delete payload.max_per_user_quantity;
    }
    if (!payload.brand_id) delete payload.brand_id;
    if (!payload.set_id) delete payload.set_id;
    if (!payload.item_image_url?.trim()) delete payload.item_image_url;
    dispatch(updatePreQuotedCooks(payload)).then((res) => {
      setShowLoader(false);
      if (res?.error && Object.keys(res.error).length > 0) { showToast(res.error.message, "error"); return; }
      onSuccess();
    });
  };

  const isFormIncomplete =
    !formData.item_link || !formData.item_name || !formData.upc || !formData.cashout_price ||
    !formData.retailer_name || !formData.description || formData.buy_price === "" ||
    parseFloat(formData.buy_price) <= 0 || !formData.total_quantity ||
    !formData.brand_id ||
    !!formErrors.max_per_user_quantity || !!formErrors.upc || !!formErrors.buy_price;

  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div
          style={{
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 12,
            padding: 14,
            background: "rgba(255,255,255,0.02)",
          }}
        >
          <div style={{ color: "#fff", fontSize: 13, fontWeight: 800, marginBottom: 12 }}>
            Catalog Placement
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
            <div>
              <Label htmlFor="brand_id">Brand *</Label>
              <CatalogSelect
                id="brand_id"
                value={formData.brand_id}
                placeholder="Select a brand"
                options={brands}
                onValueChange={(brandId) => setFormData((p) => ({ ...p, brand_id: brandId, set_id: "" }))}
              />
            </div>
            <div>
              <Label htmlFor="set_id">Set</Label>
              <CatalogSelect
                id="set_id"
                value={formData.set_id}
                placeholder={formData.brand_id ? "Select a set" : "Select a brand first"}
                emptyLabel="No set"
                options={sets}
                disabled={!formData.brand_id}
                onValueChange={(setId) => setFormData((p) => ({ ...p, set_id: setId }))}
              />
            </div>
          </div>
        </div>
        <div>
          <Label htmlFor="upc">UPC</Label>
          <Input id="upc" name="upc" value={formData.upc} placeholder="Enter UPC" onChange={handleFormData} onWheel={(e) => e.target.blur()} />
          {formErrors.upc && <p style={{ fontSize: 11, color: "#f27059", marginTop: 4 }}>{formErrors.upc}</p>}
        </div>
        <div>
          <Label htmlFor="item_name">Item Name</Label>
          <Input id="item_name" name="item_name" value={formData.item_name} placeholder="Item Name" onChange={handleFormData} />
        </div>
        <div>
          <Label htmlFor="item_link">Item Link</Label>
          <Input id="item_link" name="item_link" value={formData.item_link} placeholder="Enter Product Link" onChange={handleFormData} />
        </div>
        <div>
          <Label htmlFor="item_image_url">Image URL</Label>
          <Input id="item_image_url" name="item_image_url" value={formData.item_image_url} placeholder="Paste product image link" onChange={handleFormData} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
          <div>
            <Label htmlFor="total_quantity">Quantity</Label>
            <Input id="total_quantity" type="number" name="total_quantity" value={formData.total_quantity} placeholder="Quantity" onChange={handleFormData} onKeyDown={blockInvalidInt} onPaste={(e) => { if (!/^\d+$/.test(e.clipboardData.getData("text"))) e.preventDefault(); }} onWheel={(e) => e.target.blur()} />
            {formErrors.total_quantity && <p style={{ fontSize: 11, color: "#f27059", marginTop: 4 }}>{formErrors.total_quantity}</p>}
          </div>
          <div>
            <Label htmlFor="max_per_user_quantity">Max Qty Per User</Label>
            <Input id="max_per_user_quantity" type="number" name="max_per_user_quantity" value={formData.max_per_user_quantity} placeholder="Max Qty" onChange={handleFormData} onKeyDown={blockInvalidInt} onPaste={(e) => { if (!/^\d+$/.test(e.clipboardData.getData("text"))) e.preventDefault(); }} onWheel={(e) => e.target.blur()} />
            {formErrors.max_per_user_quantity && <p style={{ fontSize: 11, color: "#f27059", marginTop: 4 }}>{formErrors.max_per_user_quantity}</p>}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
          <div>
            <Label htmlFor="buy_price">Buy Price</Label>
            <Input id="buy_price" type="number" name="buy_price" value={formData.buy_price} placeholder="Buy Price" onChange={handleFormData} onKeyDown={blockInvalidPrice} onPaste={(e) => { if (!/^\d*\.?\d*$/.test(e.clipboardData.getData("text"))) e.preventDefault(); }} onWheel={(e) => e.target.blur()} />
            {formErrors.buy_price && <p style={{ fontSize: 11, color: "#f27059", marginTop: 4 }}>{formErrors.buy_price}</p>}
          </div>
          <div>
            <Label htmlFor="cashout_price">Cash-Out Price</Label>
            <Input id="cashout_price" type="number" name="cashout_price" value={formData.cashout_price} placeholder="Cash-Out Price" onChange={handleFormData} onKeyDown={blockInvalidPrice} onPaste={(e) => { if (!/^\d*\.?\d*$/.test(e.clipboardData.getData("text"))) e.preventDefault(); }} onWheel={(e) => e.target.blur()} />
            {formErrors.cashout_price && <p style={{ fontSize: 11, color: "#f27059", marginTop: 4 }}>{formErrors.cashout_price}</p>}
          </div>
        </div>
        <div>
          <Label htmlFor="retailer_name">Retailer Name</Label>
          <Input id="retailer_name" name="retailer_name" value={formData.retailer_name} placeholder="Enter Retailer Name" onChange={handleFormData} />
        </div>
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea id="description" name="description" value={formData.description} placeholder="Enter Description" onChange={handleFormData} />
        </div>
        <div>
          <Label htmlFor="tags">Tags</Label>
          <Input id="tags" name="tags" value={formData.tags} placeholder="Enter Tags" onChange={handleFormData} />
        </div>
        <div>
          <Label htmlFor="status">Status</Label>
          <Select id="status" name="status" value={formData.status} onChange={handleFormData}>
            <SelectOption value="ACTIVE">ACTIVE</SelectOption>
            <SelectOption value="INACTIVE">INACTIVE</SelectOption>
          </Select>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Button disabled={isFormIncomplete || showLoader} onClick={handleSubmit}>
          {showLoader ? "Updating…" : "Update"}
        </Button>
      </div>
    </div>
  );
};

const PreQuotedCooksPage = () => {
  const [openDeletePrequote, setOpenDeletePrequote] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [detailRow, setDetailRow] = useState(null);
  const [imgPreviewOpen, setImgPreviewOpen] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [nameExpanded, setNameExpanded] = useState(false);
  const [preQuoteDetails, setPreQuoteDetails] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const stored = sessionStorage.getItem("prequotedCooks_selectedStatuses");
    return stored ? JSON.parse(stored) : [];
  });
  const [sortDirection, setSortDirection] = useState(() => sessionStorage.getItem("prequotedCooks_sortDirection") || null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [createOpen, setCreateOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [brandOptions, setBrandOptions] = useState([]);
  const [setFilterOptions, setSetFilterOptions] = useState([]);
  const [selectedBrandFilter, setSelectedBrandFilter] = useState("");
  const [selectedSetFilter, setSelectedSetFilter] = useState("");

  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const getAllPQCDetails = () => {
    setShowLoader(true);
    dispatch(listAllPreQuotedCooks()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      const formatted = res?.payload?.map((item) => ({
        ...item,
        buyPrice: formatNumberToDollar(item?.buy_price),
        cashoutPrice: formatNumberToDollar(item?.cashout_price),
      }));
      setShowLoader(false);
      setPreQuoteDetails(formatted);
    });
  };

  const loadBrandOptions = () => {
    dispatch(listAllPreQuoteBrands()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      setBrandOptions(res?.payload || []);
    });
  };

  useEffect(() => { getAllPQCDetails(); }, [isRefresh]);

  useEffect(() => {
    loadBrandOptions();
  }, [dispatch, showToast]);

  useEffect(() => {
    setSelectedSetFilter("");
    if (!selectedBrandFilter) {
      setSetFilterOptions([]);
      return;
    }
    dispatch(listAllPreQuoteSets({ brand_id: selectedBrandFilter })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      setSetFilterOptions(res?.payload || []);
    });
  }, [dispatch, selectedBrandFilter, showToast]);

  const filterData = () => {
    let filtered = [...preQuoteDetails];
    if (searchTerm.trim()) {
      const t = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          (item.upc && String(item.upc).toLowerCase().includes(t)) ||
          (item.retailer_name && item.retailer_name.toLowerCase().includes(t)) ||
          (item.item_name && item.item_name.toLowerCase().includes(t))
      );
    }
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((item) => selectedStatuses.includes(item?.status));
    }
    if (selectedBrandFilter) {
      filtered = filtered.filter((item) => item?.brand_id === selectedBrandFilter);
    }
    if (selectedSetFilter) {
      filtered = filtered.filter((item) => item?.set_id === selectedSetFilter);
    }
    if (sortDirection === "asc") filtered.sort((a, b) => Number(a?.total_quantity || 0) - Number(b?.total_quantity || 0));
    else if (sortDirection === "desc") filtered.sort((a, b) => Number(b?.total_quantity || 0) - Number(a?.total_quantity || 0));
    setFilteredData(filtered);
    setCurrentPage(0);
  };

  useEffect(() => { filterData(); }, [preQuoteDetails, selectedStatuses, sortDirection, searchTerm, selectedBrandFilter, selectedSetFilter]);

  useEffect(() => {
    sessionStorage.setItem("prequotedCooks_selectedStatuses", JSON.stringify(selectedStatuses));
  }, [selectedStatuses]);

  useEffect(() => {
    if (sortDirection) sessionStorage.setItem("prequotedCooks_sortDirection", sortDirection);
    else sessionStorage.removeItem("prequotedCooks_sortDirection");
  }, [sortDirection]);

  const handleStatusChange = (status, checked) => {
    setSelectedStatuses((prev) => (checked ? [...prev, status] : prev.filter((s) => s !== status)));
  };

  const handlePressDelete = (row) => { setSelectedRow(row); setOpenDeletePrequote(true); };

  const handleDeletePreQuote = () => {
    setShowLoader(true);
    dispatch(updatePreQuotedCooksStatus({ id: selectedRow.id, status: "INACTIVE" })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) { setShowLoader(false); showToast(res?.error?.message, "error"); return; }
      setIsRefresh(!isRefresh);
      setShowLoader(false);
      setOpenDeletePrequote(false);
    });
  };

  const pqcStats = useMemo(() => {
    const active = preQuoteDetails.filter((d) => d.status === "ACTIVE").length;
    const inactive = preQuoteDetails.filter((d) => d.status === "INACTIVE").length;
    return { active, inactive, total: preQuoteDetails.length };
  }, [preQuoteDetails]);

  const activeFilterCount =
    countActiveFiltersAndSort(sortDirection, selectedStatuses) +
    (selectedBrandFilter ? 1 : 0) +
    (selectedSetFilter ? 1 : 0);
  const paginatedRows = filteredData.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

  const columns = [
    {
      key: "item_name",
      label: "Item Name",
      width: 210,
      maxWidth: 210,
      render: (row) => <span style={{ color: "#fff", fontWeight: 500 }}>{row?.item_name}</span>,
    },
    { key: "upc", label: "UPC", width: 130 },
    { key: "retailer_name", label: "Retailer Name", width: 140 },
    { key: "brand_name", label: "Brand", width: 120, render: (row) => row?.brand_name || "Uncategorized" },
    { key: "set_name", label: "Set", width: 120, render: (row) => row?.set_name || "No set" },
    { key: "total_quantity", label: "Qty", width: 64, align: "center" },
    { key: "buyPrice", label: "Buy Price", width: 90, align: "right" },
    { key: "cashoutPrice", label: "Cashout Price", width: 110, align: "right" },
    {
      key: "status",
      label: "Status",
      width: 96,
      align: "center",
      render: (row) => <StatusBadge status={row?.status} />,
    },
    {
      key: "action",
      label: "Action",
      width: 110,
      align: "center",
      render: (row) => (
        <div style={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Button variant="ghost" size="icon-sm" title="View" onClick={(e) => { e.stopPropagation(); setImgError(false); setNameExpanded(false); setDetailRow(row); }}>
            <Eye size={15} />
          </Button>
          <Button variant="ghost" size="icon-sm" title="Edit" onClick={() => setEditData(row)}>
            <FilePenLine size={15} />
          </Button>
          {row?.status === "ACTIVE" && (
            <Button variant="ghost" size="icon-sm" title="Delete" onClick={() => handlePressDelete(row)}>
              <Trash2 size={15} style={{ color: "#f27059" }} />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div style={{ maxWidth: 1200, margin: "0 auto", width: "100%" }}>
        <div style={{ marginBottom: 20 }}>
          <PageHeader
            title="Sell Catalog"
            subtitle="Review and manage member-facing sell products with pre-defined pricing."
          />
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? "145px" : "180px"}, 1fr))`,
            gap: isMobile ? 12 : 16,
            marginBottom: 20,
          }}
        >
          <div style={statStyle("#4caf50")}>
            <span style={statLabelStyle}>ACTIVE</span>
            <span style={statValueStyle}>{pqcStats.active}</span>
          </div>
          <div style={statStyle("#f27059")}>
            <span style={statLabelStyle}>INACTIVE</span>
            <span style={statValueStyle}>{pqcStats.inactive}</span>
          </div>
          <div style={statStyle("#00ADB5")}>
            <span style={statLabelStyle}>TOTAL</span>
            <span style={statValueStyle}>{pqcStats.total}</span>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: isMobile ? "stretch" : "center",
              gap: isMobile ? 10 : 12,
              flexWrap: "wrap",
              width: isMobile ? "100%" : "auto",
            }}
          >
            {!isMobile && <RefreshButton onRefresh={getAllPQCDetails} spinning={showLoader} />}
            <div style={{ flex: isMobile ? 1 : "none", minWidth: isMobile ? 0 : undefined }}>
              <SearchBar
                placeholder="Search by UPC, retailer, name…"
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="default" style={{ width: isMobile ? "100%" : "auto" }}>
                  <Filter size={14} />
                  Filter & Sort
                  {activeFilterCount > 0 && (
                    <span
                      style={{
                        marginLeft: 4,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 16,
                        height: 16,
                        borderRadius: 8,
                        background: "#00ADB5",
                        color: "#000",
                        fontSize: 10,
                        fontWeight: 800,
                        padding: "0 4px",
                      }}
                    >
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" style={{ minWidth: 220 }}>
                <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                {PQC_STATUSES.map((s) => (
                  <DropdownMenuCheckboxItem
                    key={s}
                    checked={selectedStatuses.includes(s)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(checked) => handleStatusChange(s, checked)}
                  >
                    {s}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Filter by Brand</DropdownMenuLabel>
                <DropdownMenuItem
                  active={!selectedBrandFilter}
                  onClick={() => {
                    setSelectedBrandFilter("");
                    setSelectedSetFilter("");
                  }}
                >
                  All Brands
                </DropdownMenuItem>
                {brandOptions.map((brand) => (
                  <DropdownMenuItem
                    key={brand.id}
                    active={selectedBrandFilter === brand.id}
                    onClick={() => setSelectedBrandFilter(brand.id)}
                  >
                    {brand.name}
                  </DropdownMenuItem>
                ))}
                {selectedBrandFilter && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>Filter by Set</DropdownMenuLabel>
                    <DropdownMenuItem active={!selectedSetFilter} onClick={() => setSelectedSetFilter("")}>
                      All Sets
                    </DropdownMenuItem>
                    {setFilterOptions.map((set) => (
                      <DropdownMenuItem
                        key={set.id}
                        active={selectedSetFilter === set.id}
                        onClick={() => setSelectedSetFilter(set.id)}
                      >
                        {set.name}
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem active={sortDirection === "asc"} onClick={() => setSortDirection((prev) => prev === "asc" ? null : "asc")}>
                  <AArrowUp size={14} style={{ marginRight: 8 }} /> Sort Qty (Low to High)
                </DropdownMenuItem>
                <DropdownMenuItem active={sortDirection === "desc"} onClick={() => setSortDirection((prev) => prev === "desc" ? null : "desc")}>
                  <AArrowDown size={14} style={{ marginRight: 8 }} /> Sort Qty (High to Low)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={() => setCreateOpen(true)} style={{ width: isMobile ? "100%" : "auto" }}>
              Add Sell Product
            </Button>
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={paginatedRows}
          totalRows={filteredData.length}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          onPageChange={(n) => setCurrentPage(n)}
          onRowsPerPageChange={(n) => { setRowsPerPage(n); setCurrentPage(0); }}
          loading={showLoader}
          minWidth={1140}
        />
      </div>

      {/* Detail dialog */}
      <Dialog open={Boolean(detailRow)} onOpenChange={(open) => !open && setDetailRow(null)}>
        <DialogContent
          style={{
            maxWidth: 560,
            width: isMobile ? "calc(100vw - 16px)" : "min(560px, calc(100vw - 64px))",
            background: "#111",
            padding: isMobile ? 16 : 24,
          }}
        >
          <DialogHeader>
            <DialogTitle>Sell Product Details</DialogTitle>
            <DialogDescription>View details for this sell catalog product.</DialogDescription>
          </DialogHeader>
          {detailRow && (
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "rgba(255,255,255,0.03)",
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 16,
                  gap: 12,
                }}
              >
                <p
                  onClick={() => setNameExpanded(!nameExpanded)}
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "#fff",
                    margin: 0,
                    flex: 1,
                    cursor: "pointer",
                    display: "-webkit-box",
                    WebkitLineClamp: nameExpanded ? "unset" : 3,
                    WebkitBoxOrient: "vertical",
                    overflow: nameExpanded ? "visible" : "hidden",
                  }}
                >
                  {detailRow?.item_name}
                </p>
                {imgError || !detailRow?.item_image_url ? (
                  <div
                    style={{
                      width: 100,
                      height: 100,
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.1)",
                      flexShrink: 0,
                      background: "rgba(255,255,255,0.05)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#8b9296",
                      fontSize: 11,
                      textAlign: "center",
                      padding: 8,
                    }}
                  >
                    Image unavailable
                  </div>
                ) : (
                  <img
                    src={detailRow.item_image_url}
                    alt={detailRow.item_name}
                    onError={() => setImgError(true)}
                    onClick={() => setImgPreviewOpen(true)}
                    style={{
                      width: 100,
                      height: 100,
                      objectFit: "contain",
                      background: "#fff",
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.1)",
                      flexShrink: 0,
                      cursor: "pointer",
                    }}
                  />
                )}
              </div>

              {[
                { label: "UPC", value: detailRow?.upc },
                { label: "Retailer", value: detailRow?.retailer_name },
                { label: "Brand", value: detailRow?.brand_name || "Uncategorized" },
                { label: "Set", value: detailRow?.set_name || "No set" },
                { label: "Buy Price", value: detailRow?.buyPrice },
                { label: "Cashout Price", value: detailRow?.cashoutPrice },
                { label: "Total Quantity", value: detailRow?.total_quantity },
                { label: "Max Per User", value: detailRow?.max_per_user_quantity },
                { label: "Status", value: detailRow?.status ? <StatusBadge status={detailRow.status} /> : null },
                { label: "Tags", value: detailRow?.tags },
                { label: "Description", value: detailRow?.description },
                {
                  label: "Item Link",
                  value: detailRow?.item_link ? (
                    <a href={detailRow.item_link} target="_blank" rel="noopener noreferrer" style={{ color: "#00ADB5", textDecoration: "none", fontSize: 13 }}>
                      View item →
                    </a>
                  ) : null,
                },
              ]
                .filter((item) => item.value !== null && item.value !== undefined && item.value !== "")
                .map((item, i) => (
                  <div
                    key={i}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                      padding: "9px 0",
                      borderBottom: "1px solid rgba(255,255,255,0.05)",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontSize: 10, color: "#8b9296", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 800 }}>{item.label}</span>
                    <span style={{ fontSize: 13, color: "#fff", textAlign: "right" }}>{item.value}</span>
                  </div>
                ))}

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const row = detailRow;
                    setDetailRow(null);
                    setEditData(row);
                  }}
                >
                  <FilePenLine size={14} style={{ marginRight: 6 }} /> Edit
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image preview dialog */}
      <Dialog open={imgPreviewOpen} onOpenChange={setImgPreviewOpen}>
        <DialogContent style={{ maxWidth: 480, background: "#111", padding: 16 }}>
          <DialogHeader>
            <DialogTitle>Item Image</DialogTitle>
            <DialogDescription>{detailRow?.item_name}</DialogDescription>
          </DialogHeader>
          <img
            src={detailRow?.item_image_url}
            alt={detailRow?.item_name}
            style={{ width: "100%", borderRadius: 8, objectFit: "contain", maxHeight: 400 }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={openDeletePrequote} onOpenChange={setOpenDeletePrequote}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Soft Delete</DialogTitle>
            <DialogDescription>
              You are about to delete this Sell Product. Once done, the item will not be visible for members. Are you sure you want to proceed?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDeletePrequote(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeletePreQuote}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={(open) => !open && setCreateOpen(false)}>
        <DialogContent style={{ maxWidth: 600, width: isMobile ? "calc(100vw - 16px)" : "90vw" }}>
          <DialogHeader>
            <DialogTitle>Add Sell Product</DialogTitle>
            <DialogDescription>Create a new sell catalog product with pre-defined pricing.</DialogDescription>
          </DialogHeader>
          {createOpen && (
            <PqcCreateForm
              showToast={showToast}
              isMobile={isMobile}
              onSuccess={() => { setCreateOpen(false); getAllPQCDetails(); loadBrandOptions(); }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={Boolean(editData)} onOpenChange={(open) => !open && setEditData(null)}>
        <DialogContent style={{ maxWidth: 600, width: isMobile ? "calc(100vw - 16px)" : "90vw" }}>
          <DialogHeader>
            <DialogTitle>Edit Sell Product</DialogTitle>
            <DialogDescription>Update the sell catalog product details below.</DialogDescription>
          </DialogHeader>
          {editData && (
            <PqcEditForm
              editData={editData}
              showToast={showToast}
              onSuccess={() => { setEditData(null); getAllPQCDetails(); loadBrandOptions(); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const statStyle = (accent) => ({
  background: "linear-gradient(180deg, rgba(255,255,255,0.025), transparent)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderTop: `3px solid ${accent}`,
  borderRadius: 14,
  padding: "16px 18px",
});


const statLabelStyle = {
  display: "block",
  color: "#c3c8cc",
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: "0.09em",
};

const statValueStyle = {
  display: "block",
  color: "#fff",
  fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
  fontSize: 24,
  fontWeight: 800,
  marginTop: 8,
};

export default PreQuotedCooksPage;
